import { useMutation, useQuery } from '@tanstack/react-query';
import axiosInstance from './axios';
import { SalesInvoiceReponseT } from '../types/quatationType';

export type CreateDeliveryNoteRequest = {
  invoiceId: number;
  items: {
    invoiceItemId: number;
    quantity: number;
    unit: string;
    conversionToBaseUnit: number;
    baseQuantity: number;
  }[];
};

export const useAddInvoice = (options = {}) => {
  return useMutation({
    mutationKey: ['useAddQuotation'],
    mutationFn: async (postData: any) => {
      const { data } = await axiosInstance.post('sales/invoice/save', postData);
      return data;
    },
    ...options,
  });
};

export const useGetAllInvoice = (postData: any) => {
  return useQuery({
    queryKey: ['useGetAllInvoice', postData],
    queryFn: async () => {
      const payload: Record<string, any> = {
        pageNo: postData.pageNo ?? 0,
        pageSize: postData.pageSize ?? 10,
      };
      if (postData.keyword) payload.keyword = postData.keyword;
      if (postData.invoiceNumber) payload.invoiceNumber = postData.invoiceNumber;
      if (postData.paymentStatus) payload.paymentStatus = postData.paymentStatus;
      if (postData.customerId) payload.customerId = postData.customerId;
      if (postData.staffId) payload.staffId = postData.staffId;
      if (postData.branchId) payload.branchId = postData.branchId;
      if (postData.counterId) payload.counterId = postData.counterId;

      const { data } = await axiosInstance.post('sales/invoice/all', payload);
      return data;
    },
  });
};

export const useRevokeInvoice = (options = {}) => {
  return useMutation({
    mutationKey: ['useRevokeInvoice'],
    mutationFn: async (postData: { invoiceId: string }) => {
      const { data } = await axiosInstance.post(
        `sales/invoice/revoke?invoiceId=${postData.invoiceId}`
      );
      return data;
    },
    ...options,
  });
};

export const usePrintInvoice = (options = {}) => {
  return useMutation({
    mutationKey: ['usePrintInvoice'],
    mutationFn: async ({ invoiceId }: { invoiceId: string }) => {
      const { data } = await axiosInstance.get(`pdf/invoice/${invoiceId}/html`);
      return data;
    },
    ...options,
  });
};

export const useDeliveryNotePrint = (options = {}) => {
  return useMutation({
    mutationKey: ['useDeliveryNotePrint'],
    mutationFn: async ({ invoiceId }: { invoiceId: string }) => {
      const { data } = await axiosInstance.get(`pdf/packing-slip/${invoiceId}`);
      return data;
    },
    ...options,
  });
};

export const useCreateDeliveryNote = (options = {}) => {
  return useMutation({
    mutationKey: ['useCreateDeliveryNote'],
    mutationFn: async (postData: CreateDeliveryNoteRequest) => {
      const { data } = await axiosInstance.post(
        '/delivery-note/create',
        postData
      );
      return data;
    },
    ...options,
  });
};

export const useDownloadDeliveryNotePdf = (options = {}) => {
  return useMutation({
    mutationKey: ['useDownloadDeliveryNotePdf'],
    mutationFn: async ({ deliveryNoteId }: { deliveryNoteId: string }) => {
      const { data } = await axiosInstance.get(
        `/delivery-note/${deliveryNoteId}/pdf`,
        { responseType: 'blob' }
      );
      return data as Blob;
    },
    ...options,
  });
};

export type DeliveryNoteItem = {
  invoiceItemId: number;
  quantity: number;
};

export type DeliveryNoteResponse = {
  deliveryNoteId: number;
  invoiceNumber: string;
  customerName: string;
  createdByName: string;
  createdAt: string;
  totalDeliveredQuantity: number;
};

export const useGetDeliveryNotesByInvoice = (
  invoiceId: string | undefined,
  params: { page?: number; size?: number } = {}
) => {
  return useQuery({
    queryKey: ['useGetDeliveryNotesByInvoice', invoiceId, params],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/delivery-note/all`, {
        params: {
          invoiceId,
          page: params.page ?? 0,
          size: params.size ?? 50,
        },
      });
      return (data?.content ?? []) as DeliveryNoteResponse[];
    },
    enabled: !!invoiceId,
  });
};

export const useGetASalesInvoice = (invoiceId: string | undefined) => {
  return useQuery({
    queryKey: ['useGetASalesInvoice', invoiceId],
    queryFn: async () => {
      const { data } = await axiosInstance.get(`/sales/invoice/${invoiceId}`);
      return data as SalesInvoiceReponseT;
    },
    enabled: !!invoiceId,
  });
};

export const useDownloadInvoicePdf = () => {
  return useMutation({
    mutationKey: ['useDownloadInvoicePdf'],
    mutationFn: async ({ invoiceId, isDraft }: { invoiceId: string | number; isDraft: boolean }) => {
      const response = await axiosInstance.get(
        `sales/invoice-pdf/${invoiceId}`,
        {
          params: { isDraft },
          responseType: 'blob',
        }
      );
      return response.data as Blob;
    },
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            try {
              printWindow.print();
            } catch (e) {
              console.error('Auto-print blocked, user can print manually', e);
            }
          }, 500);
        };
      }
    },
  });
};
